import { ref, computed, onMounted } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import type { LsPlgUsage } from '@eureka/services/src/apis/common.api.types';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { controlDeferred } from '@eureka/utils/control';
import { openClueReport } from '@eureka/shared/clue-report';
import dayjs from 'dayjs';

/**
 * LS 版本类型
 */
type LsEditionType = 'enterprise' | 'team' | 'personal_pro' | 'free_trial' | 'personal_basic' | 'unknown';

/**
 * 用量项数据结构
 */
interface UsageItem {
    current: number;
    max: number;
    progress: number;
}

/**
 * LS 版本用量管理 - 整合业务逻辑和 API 调用
 */
export const useLsEditionUsage = createSharedComposable(() => {
    const lsPlgUsage = ref<LsPlgUsage | null>(null);
    const isLoading = ref(false);
    const error = ref<Error | null>(null);

    const controlInfo = ref()

    onMounted(async() => {
        controlInfo.value = await controlDeferred.promise
    })

    /**
     * 计算进度百分比
     */
    function getProgressPercentage(current: number = 0, max: number = 0): number {
        if (!max || max === 0) return 0;
        return Math.min(Math.round((current / max) * 100), 100);
    }

    // ========== 用户版本判断 ==========
    const userEdition = computed<LsEditionType>(() => {
        return controlInfo.value?.ls_plg_role || 'unknown';
    });

    const editionBadgeText = computed(() => {
        const map: Record<LsEditionType, string> = {
            enterprise: 'Enterprise',
            team: 'Team',
            personal_pro: 'Pro',
            free_trial: 'Trial',
            personal_basic: 'Basic',
            unknown: '--',
        };
        return map[userEdition.value];
    });

    // ========== Pro 用户判断（付费用户：enterprise/team/personal_pro）==========
    const isLsPro = computed(() => {
        const proTypes: LsEditionType[] = ['enterprise', 'team', 'personal_pro'];
        return proTypes.includes(userEdition.value);
    });

    // ========== Usage 数据获取 ==========

    /**
     * Earn Credits 用量
     */
    const earnCredits = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'reword_credit')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * Credits/month 用量
     */
    const creditsPerMonth = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'privilege_credit')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * Projects 用量（MB）
     */
    const projects = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'project_storage')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 过期日期倒计时天数
     * 规则：
     * 1. personal_basic 用户不展示：返回 null
     * 2. 其他用户：如果过期日期 <= 14天，返回剩余天数（number）
     * 3. 过期日期包含当天（即过期日当天还能使用），所以最小显示为 1 day left
     * 4. 其他情况返回 null
     */
    const expireDaysLeft = computed<number | null>(() => {
        // personal_basic 用户不展示
        if (userEdition.value === 'personal_basic') {
            return null;
        }

        // 没有用量数据或没有过期日期
        if (!lsPlgUsage.value?.role_expire_at) {
            return null;
        }

        // role_expire_at 格式为 YYYY-MM-DD，统一到当天开始时间进行比较
        const expireAt = dayjs(lsPlgUsage.value.role_expire_at).startOf('day');
        const today = dayjs().startOf('day');
        const daysLeft = expireAt.diff(today, 'day') + 1;

        // 只有 <= 14 天时才展示
        if (daysLeft <= 0) {
            return null; // 已过期
        }

        if (daysLeft <= 14) {
            return daysLeft;
        }

        return null;
    });

    // ========== 展示逻辑 ==========

    /**
     * Earn Credits: 有用量则展示
     */
    const showEarnCredits = computed(() => {
        if (!earnCredits.value) return false;
        const { max, current } = earnCredits.value;
        return max > 0 || current > 0;
    });

    /**
     * Credits/month: 有最大额度则展示
     */
    const showCreditsPerMonth = computed(() => {
        if (!creditsPerMonth.value) return false;
        return creditsPerMonth.value.max > 0;
    });

    /**
     * Projects: 有最大额度则展示
     */
    const showProjects = computed(() => {
        if (!projects.value) return false;
        return projects.value.max > 0;
    });

    /**
     * 是否有任何用量项
     */
    const hasAnyUsageItems = computed(() => {
        return showEarnCredits.value || showCreditsPerMonth.value || showProjects.value;
    });

    /**
     * Upgrade 按钮: 仅免费用户和试用用户显示
     */
    const showUpgradeButton = computed(() => {
        const upgradeTypes: LsEditionType[] = ['free_trial', 'personal_basic'];
        return upgradeTypes.includes(userEdition.value);
    });

    // ========== 用量上限检查 ==========

    /**
     * 积分用量是否达到上限
     * 检查 earnCredits 或 creditsPerMonth 是否任一达到上限
     */
    const isCreditLimitReached = computed(() => {
        // 检查 Earn Credits
        if (earnCredits.value) {
            const { current, max } = earnCredits.value;
            if (max > 0 && current >= max) return true;
        }

        // 检查 Credits/month
        if (creditsPerMonth.value) {
            const { current, max } = creditsPerMonth.value;
            if (max > 0 && current >= max) return true;
        }

        return false;
    });

    /**
     * Project 用量是否达到上限
     */
    const isProjectStorageLimitReached = computed(() => {
        if (!projects.value) return false;
        const { current, max } = projects.value;
        return max > 0 && current >= max;
    });

    // ========== API 调用 ==========

    /**
     * 获取 LS PLG 用量数据
     */
    async function fetchLsPlgUsage() {
        isLoading.value = true;
        error.value = null;

        try {
            const res = await CommonApis.getLsPlgUsage();
            lsPlgUsage.value = res;
        } catch (e) {
            error.value = e as Error;
            console.error('[useLsEditionUsage] Failed to fetch LS PLG usage:', e);
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * 升级逻辑 - 跳转到 LS 套餐页面
     */
    const handleUpgrade = () => {
        location.href = '/user-setting/package/ls/plg';
    };

    /**
     * 检查用量限制并在达到上限时显示联系销售弹窗
     * @param type - 检查类型：'credit' 积分用量 或 'project_storage' 项目存储用量
     * @returns Promise<boolean> - true: 未达到上限可以继续操作，false: 已达到上限不能继续操作
     */
    const checkUsageLimitAndShowDialog = async(type: 'credit' | 'project_storage'): Promise<boolean> => {
        try {
            // 先获取最新的用量数据
            await fetchLsPlgUsage();

            // 检查是否达到上限
            let isLimitReached = false;

            if (type === 'credit') {
                isLimitReached = isCreditLimitReached.value;
            } else if (type === 'project_storage') {
                isLimitReached = isProjectStorageLimitReached.value;
            }

            // 如果达到上限，打开联系销售弹窗
            if (isLimitReached) {
                // 使用 openClueReport 打开联系销售弹窗
                await openClueReport({
                    pkgType: EurekaPackageType.LS_COMMERCIAL,
                    version: 'LS_PLG',
                });

                return false; // 达到上限，不能继续操作
            }

            return true; // 未达到上限，可以继续操作
        } catch (e) {
            console.error('[useLsEditionUsage] Failed to check usage limit:', e);
            // 如果检查失败，为了不阻止用户操作，返回 true
            return true;
        }
    };

    return {
        // 数据状态
        isLoading,
        error,
        userEdition,
        editionBadgeText,
        isLsPro,
        expireDaysLeft,

        // Usage 数据
        earnCredits,
        creditsPerMonth,
        projects,
        lsPlgUsage,

        // 展示逻辑
        showEarnCredits,
        showCreditsPerMonth,
        showProjects,
        showUpgradeButton,
        hasAnyUsageItems,

        // 用量上限检查
        isCreditLimitReached,
        isProjectStorageLimitReached,

        // 工具函数
        handleUpgrade,
        fetchLsPlgUsage,
        checkUsageLimitAndShowDialog,
    };
});
